// 重试机制配置接口
export interface RetryConfig {
  maxRetries?: number;           // 最大重试次数
  retryDelay?: number;           // 重试延迟时间（毫秒）
  backoffMultiplier?: number;    // 退避倍数
  retryCondition?: (error: any) => boolean;  // 重试条件
  onRetry?: (attempt: number, error: any) => void;  // 重试回调
}

// 默认重试配置
const defaultRetryConfig: Required<RetryConfig> = {
  maxRetries: 3,
  retryDelay: 1000,
  backoffMultiplier: 2,
  retryCondition: (error: any) => {
    // 网络错误、超时、5xx服务器错误才重试（不包括404）
    return (
      error.message === 'Network Error' ||
      error.code === 'ECONNABORTED' ||
      (error.response && error.response.status >= 500) ||
      error.code === 'ERR_NETWORK'
    );
  },
  onRetry: (attempt: number, error: any) => {
    console.warn(`请求失败，第${attempt}次重试:`, error.message);
  }
};

// 延迟函数
const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// 重试函数
export async function retryRequest<T>(
  requestFn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const finalConfig = { ...defaultRetryConfig, ...config };
  let lastError: any;

  for (let attempt = 1; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // 检查是否应该重试
      if (!finalConfig.retryCondition(error)) {
        throw error;
      }

      // 如果是最后一次尝试，直接抛出错误
      if (attempt === finalConfig.maxRetries) {
        console.error(`请求失败，已重试${finalConfig.maxRetries}次:`, error);
        throw error;
      }

      // 执行重试回调
      finalConfig.onRetry(attempt, error);

      // 计算延迟时间（指数退避）
      const delayTime = finalConfig.retryDelay * Math.pow(finalConfig.backoffMultiplier, attempt - 1);
      
      // 等待延迟
      await delay(delayTime);
    }
  }

  throw lastError;
}

// 创建带重试的请求函数
export function createRetryRequest(config: RetryConfig = {}) {
  return async function<T>(requestFn: () => Promise<T>): Promise<T> {
    return retryRequest(requestFn, config);
  };
}

// 预定义的重试配置
export const retryConfigs = {
  // 快速重试（适用于用户交互）
  fast: {
    maxRetries: 2,
    retryDelay: 500,
    backoffMultiplier: 1.5,
  },
  // 标准重试
  standard: {
    maxRetries: 3,
    retryDelay: 1000,
    backoffMultiplier: 2,
  },
  // 保守重试（适用于重要数据）
  conservative: {
    maxRetries: 5,
    retryDelay: 2000,
    backoffMultiplier: 2,
  },
  // 仅网络错误重试
  networkOnly: {
    maxRetries: 3,
    retryDelay: 1000,
    backoffMultiplier: 2,
    retryCondition: (error: any) => {
      return error.message === 'Network Error' || error.code === 'ERR_NETWORK';
    },
  },
};


